import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, RefreshCw, Check, X } from 'lucide-react';
import { toast as toastify } from 'react-toastify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UsersTable = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingUserData, setEditingUserData] = useState({ name: '', email: '', role: 'user' });
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const { toast } = useToast();

  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: "Error",
        description: "Failed to load users",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const handleUserInputChange = (e) => {
    const { name, value } = e.target;
    setUserFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddUser = async () => {
    try {
      await axios.post(`${API}/users`, userFormData, { withCredentials: true });
      
      toastify.success('User created successfully!', {
        position: "top-right",
        autoClose: 3000,
      });
      
      setUserFormData({
        name: '',
        email: '',
        password: '',
        role: 'user'
      });
      setShowAddUserForm(false);
      fetchUsers();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create user",
        variant: "destructive"
      });
    }
  };

  const handleEditUser = async (userId, updatedData) => {
    try {
      await axios.put(`${API}/users/${userId}`, updatedData, { withCredentials: true });
      
      toastify.success('User updated successfully!', {
        position: "top-right",
        autoClose: 3000,
      });
      
      setEditingUserId(null);
      fetchUsers();
    } catch (error) {
      toastify.error(error.response?.data?.detail || 'Failed to update user', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await axios.post(`${API}/users/${userId}/reset-password`, {}, { withCredentials: true });
      const newPassword = response.data.new_password;
      
      // Copy to clipboard
      navigator.clipboard.writeText(newPassword);
      
      toastify.success(`Password reset! New password copied to clipboard: ${newPassword}`, {
        position: "top-right",
        autoClose: 5000,
      });
      
    } catch (error) {
      toastify.error(error.response?.data?.detail || 'Failed to reset password', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      
      toast({
        title: "Success",
        description: "User deleted successfully"
      });
      
      fetchUsers();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete user",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-xl md:text-2xl font-bold">User Management</h2>
        <Button onClick={() => setShowAddUserForm(!showAddUserForm)} className="flex items-center gap-2 w-full sm:w-auto">
          <Plus className="w-4 h-4" />
          Add New User
        </Button>
      </div>

      {/* Add User Form */}
      {showAddUserForm && (
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6 md:mb-8">
          <h3 className="text-lg md:text-xl font-bold mb-4">Add New User</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <Input name="name" placeholder="Name" value={userFormData.name} onChange={handleUserInputChange} />
            <Input name="email" type="email" placeholder="Email" value={userFormData.email} onChange={handleUserInputChange} />
            <Input name="password" type="password" placeholder="Password" value={userFormData.password} onChange={handleUserInputChange} />
            <select
              name="role"
              value={userFormData.role}
              onChange={handleUserInputChange}
              className="px-3 py-2 border rounded-md text-sm md:text-base"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <Button onClick={handleAddUser} className="w-full sm:w-auto">Add User</Button>
            <Button variant="outline" onClick={() => setShowAddUserForm(false)} className="w-full sm:w-auto">Cancel</Button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-md overflow-x-auto">
        <div className="min-w-[600px]">
          <table className="w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((u) => {
                const isEditing = editingUserId === u.id;
                
                return (
                  <tr key={u.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isEditing ? (
                        <Input
                          value={editingUserData.name}
                          onChange={(e) => setEditingUserData(prev => ({ ...prev, name: e.target.value }))}
                          className="max-w-xs"
                        />
                      ) : (
                        u.name
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isEditing ? (
                        <Input
                          value={editingUserData.email}
                          onChange={(e) => setEditingUserData(prev => ({ ...prev, email: e.target.value }))}
                          className="max-w-xs"
                        />
                      ) : (
                        u.email
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isEditing ? (
                        <select
                          value={editingUserData.role}
                          onChange={(e) => setEditingUserData(prev => ({ ...prev, role: e.target.value }))}
                          className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      ) : (
                        <span className={`px-2 py-1 text-xs rounded ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}`}>
                          {u.role}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        {isEditing ? (
                          <>
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => {
                                handleEditUser(u.id, editingUserData);
                              }}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingUserId(null);
                                setEditingUserData({ name: '', email: '', role: 'user' });
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingUserId(u.id);
                                setEditingUserData({ name: u.name, email: u.email, role: u.role });
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleResetPassword(u.id)}
                              title="Reset Password"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteUser(u.id)}
                              disabled={u.id === currentUser?.id}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-4 text-gray-600">Total Users: {users.length}</div>
    </div>
  );
};

export default UsersTable;
